//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material'
import { ContentCopy, ExpandMore, OpenInNew } from '@mui/icons-material'

import { PoolBaseOverviewCTO, PoolFlexOverviewCTO, PoolOverviewCTO, PoolDynamicOverviewCTO } from '@shared/sharedTypes'
import React, { useState } from 'react'

import ViewLogo from '@react/components/elements/ViewLogo'
import { useOtSelector, useOtChainState } from '@react/redux/store'

import { erc20WeiToCommaDecString } from '@shared/conversions'
import { useNavigate } from 'react-router-dom'

import { PoolTypeEnum } from '@shared/Enums'

import { buildCompletePoolSummary } from '@react/lender/lenderUtils'
import { logOT } from '@shared/loggingShared'
import { buildPills } from '@react/components/elements/InterestPills'
import PoolListSkeleton from '@react/components/pools/PoolListSkeleton'
import { displayName } from '@shared/PoolBaseCTO'
import TicketPendingCard from '@react/admin/TicketPendingCard'

function BorrowerPoolCard(props: { poolOverviewCTO: PoolBaseOverviewCTO }): React.ReactElement {
  const { poolOverviewCTO } = props

  const theme = useTheme()
  const navigate = useNavigate()
  const { chainConfiguration } = useOtChainState()
  const [expanded, setExpanded] = useState(false)

  let interestPills: React.ReactElement | undefined = undefined

  if (poolOverviewCTO.poolType === PoolTypeEnum.Term) {
    const investorSummary = buildCompletePoolSummary(poolOverviewCTO as PoolOverviewCTO)
    interestPills = buildPills({ interestRate: `${investorSummary.finalizedApr}` }, theme)
  } else if (poolOverviewCTO.poolType === PoolTypeEnum.FlexibleRate) {
    interestPills = buildPills(poolOverviewCTO as PoolFlexOverviewCTO, theme)
  } else if (poolOverviewCTO.poolType === PoolTypeEnum.Dynamic) {
    interestPills = buildPills(poolOverviewCTO as PoolDynamicOverviewCTO, theme)
  }

  const handleCopyAddress = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(poolOverviewCTO.poolAddr)
  }

  return (
    <Card
      key={poolOverviewCTO.poolAddr}
      onClick={() => navigate(`/pools/${poolOverviewCTO.poolAddr}`)}
      sx={{
        marginBottom: 4,
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.1s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-1px)',
          border: `1px solid ${theme.palette.primary.main}`,
        },
      }}
    >
      <CardContent sx={{ padding: '16px' }}>
        {/* Main content */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
          <ViewLogo
            size={48}
            style={{
              marginRight: '16px',
              backgroundColor: theme.palette.background.paper,
              borderRadius: '8px',
            }}
            addrOrId={poolOverviewCTO.poolAddr}
          />

          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {displayName(poolOverviewCTO)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginBottom: 1 }}>{interestPills}</Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.25,
                    border: '1px solid #000',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#000' }}>
                    Currency: {poolOverviewCTO.liquidityTokenSymbol}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 1.5,
                    py: 0.25,
                    border: '1px solid #000',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#000' }}>
                    Term:{' '}
                    {poolOverviewCTO.poolType === PoolTypeEnum.Term && (poolOverviewCTO as PoolOverviewCTO)
                      ? `${(poolOverviewCTO as PoolOverviewCTO).activeLoanState?.durationDays || (poolOverviewCTO as PoolOverviewCTO).requestedLoanState?.durationDays || 90} days`
                      : 'Flexible'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
              <strong>
                {`${erc20WeiToCommaDecString(poolOverviewCTO.totalOutstandingLoanPrincipal || '0')} ${poolOverviewCTO.liquidityTokenSymbol}`}
              </strong>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Principal Earning Interest
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<OpenInNew />}
              onClick={(e) => {
                e.stopPropagation()
                const explorerUrl = chainConfiguration?.blockExplorerUrl
                  ? `${chainConfiguration.blockExplorerUrl}address/${poolOverviewCTO.poolAddr}`
                  : ''
                if (explorerUrl) {
                  window.open(explorerUrl, '_blank', 'noopener,noreferrer')
                }
              }}
            >
              View on explorer
            </Button>
          </Box>
          <Button
            variant="text"
            endIcon={
              <ExpandMore sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            }
            onClick={(e) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              color: theme.palette.text.secondary,
              '&:hover': {
                textDecoration: 'underline',
                backgroundColor: 'transparent',
              },
            }}
          >
            More details
          </Button>
        </Box>

        {/* More details accordion */}
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          elevation={0}
          sx={{ backgroundColor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ display: 'none' }} />
          <AccordionDetails
            sx={{
              padding: 3,
              backgroundColor: theme.palette.grey[100],
              borderRadius: 2,
              margin: '8px 0',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" sx={{ marginBottom: 3 }}>
              Vault Details
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                  Vault Address:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {`${poolOverviewCTO.poolAddr.slice(0, 6)}....${poolOverviewCTO.poolAddr.slice(-3)}`}
                  </Typography>
                  <IconButton size="small" onClick={handleCopyAddress}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                  Benchmark Rate:
                </Typography>
                <Typography variant="caption">{poolOverviewCTO.benchmarkRate || 'N/A'}</Typography>
              </Box>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 0.5 }}>
                  Advisory Fee:
                </Typography>
                <Typography variant="caption">
                  {poolOverviewCTO.advisoryFeeRate
                    ? `${Number(poolOverviewCTO.advisoryFeeRate).toFixed(0)} BPS`
                    : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </CardContent>
    </Card>
  )
}

export default function BorrowerPoolList(): React.ReactElement {
  const { isDataReady, poolOverviewCTOs } = useOtSelector((state) => state.poolState)

  const poolCTOList = Object.values(poolOverviewCTOs).flat()

  logOT.debug('poolCTOList LenderPoolOpenings', poolOverviewCTOs, poolCTOList, poolCTOList.length)
  if (!isDataReady) {
    return <PoolListSkeleton />
  }
  if (poolCTOList.length === 0) {
    return (
      <>
        <Typography align="left" sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Available loan vaults
        </Typography>
        <Typography variant="h4" gutterBottom component="div">
          No Openings
        </Typography>
      </>
    )
  }
  return (
    <Box sx={{ marginTop: 4, marginBottom: 4 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
          padding: '0 8px',
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Available loan vaults
        </Typography>
      </Box>
      <TicketPendingCard />
      <Box>
        {poolCTOList.map((poolOverviewCTO: PoolBaseOverviewCTO) => {
          return <BorrowerPoolCard key={poolOverviewCTO.poolAddr} poolOverviewCTO={poolOverviewCTO} />
        })}
      </Box>
    </Box>
  )
}
